import { useState, useEffect, useRef } from 'react';

// MODIFIED: Full state machine — covers every API response branch without
// hardcoded message strings. Server error messages are parsed from the response body.
export type WaitlistStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'duplicate'
  | 'validation_error'
  | 'rate_limited'
  | 'server_error';

interface SuccessData {
  name: string;
  email: string;
}

interface UseWaitlistReturn {
  status: WaitlistStatus;
  submit: (name: string, email: string, description?: string, os?: string) => Promise<void>;
  errorMessage: string | null;
  cooldownSeconds: number;
  successData: SuccessData | null;
  reset: () => void;
}

const API_URL = 'https://pingafter-email.vercel.app/api/waitlist';
const RATE_LIMIT_COOLDOWN = 60;

export function useWaitlist(): UseWaitlistReturn {
  const [status, setStatus] = useState<WaitlistStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Start countdown when rate limited; reset to idle at zero
  useEffect(() => {
    if (status === 'rate_limited') {
      setCooldownSeconds(RATE_LIMIT_COOLDOWN);
      timerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            timerRef.current = null;
            setStatus('idle');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [status]);

  const submit = async (
    name: string,
    email: string,
    description?: string,
    os?: string
  ): Promise<void> => {
    setStatus('loading');
    setErrorMessage(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, description, os }),
      });

      if (response.status === 201) {
        setSuccessData({ name, email });
        setStatus('success');
        return;
      }

      if (response.status === 409) {
        setStatus('duplicate');
        return;
      }

      if (response.status === 400) {
        let message = 'Invalid request. Please check your details.';
        try {
          const body = await response.json();
          if (body?.message) message = body.message;
          else if (body?.error) message = body.error;
        } catch {
          // body not JSON — keep default
        }
        setErrorMessage(message);
        setStatus('validation_error');
        return;
      }

      if (response.status === 429) {
        setStatus('rate_limited');
        return;
      }

      // All other status codes (5xx, etc.)
      setStatus('server_error');
    } catch (error) {
      // Network failure or CORS issue
      console.error('Waitlist submission failed:', error);
      setStatus('server_error');
    }
  };

  const reset = () => {
    setStatus('idle');
    setErrorMessage(null);
    setCooldownSeconds(0);
    setSuccessData(null);
  };

  return { status, submit, errorMessage, cooldownSeconds, successData, reset };
}
